import Image from 'next/image'
import {FC} from 'react'

import {Text} from '@/components/common/content/Text'
import {getClassAssistItemContent, IpoDataKey} from '@/content/classAssist'
import {countryCards} from '@/content/countryCards'
import {useTranslation} from '@/i18n'
import {theme} from '@/theme/theme'
import {MOBILE_SCREENSIZE} from '@/utils/styles/breakpoints'

type ClassAssistItemIpoTableProps = {
  itemMap: Record<IpoDataKey, string>
}

export const ClassAssistItemIpoTable: FC<React.PropsWithChildren<ClassAssistItemIpoTableProps>> = ({
  itemMap,
}) => {
  const {t} = useTranslation()
  const {tableHeaderOffice, tableHeaderItem, offices} = getClassAssistItemContent().ipo

  return (
    <>
      <table className="ipo-table">
        <thead>
          <tr>
            <th>
              <Text>{t(tableHeaderOffice)}</Text>
            </th>
            <th>
              <Text>{t(tableHeaderItem)}</Text>
            </th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(offices).map(([key, {country, dataKey}]) => (
            <tr key={dataKey}>
              <td>
                <div className="title">
                  <div className="flag">
                    <Image
                      src={countryCards[country].imageSrc}
                      alt={countryCards[country].imageAlt}
                      style={{objectFit: 'contain'}}
                      width={32}
                      height={32}
                    />
                  </div>
                  <Text>{key}</Text>
                </div>
              </td>
              <td>
                <Text>{itemMap[dataKey] ?? 'N/A'}</Text>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <style jsx>{`
        .ipo-table {
          border-collapse: separate;
          margin-top: ${theme.spacing[5]};
        }
        th {
          padding: calc(${theme.spacing[3]} - ${theme.spacing[1]});
          background-color: ${theme.palette.background.butter};
        }
        td {
          border: 1px solid ${theme.palette.background.butter};
          border-style: none none solid none;
          vertical-align: top;
          padding: calc(${theme.spacing[3]} - ${theme.spacing[1]});
        }
        tr td:first-child {
          width: 290px;
          border-left-style: solid;
        }
        tr td:last-child {
          border-right-style: solid;
          padding-top: ${theme.spacing[3]};
        }
        .title {
          display: flex;
          align-items: center;
        }
        .flag {
          position: relative;
          width: 32px;
          height: 32px;
          flex: 0 0 32px;
          margin-right: ${theme.spacing[3]};
          border-radius: 50%;
          box-shadow: 0px 0px 0px 1px ${theme.palette.gray[200]};
        }
        @media ${MOBILE_SCREENSIZE} {
          .ipo-table {
            margin-top: -${theme.spacing[2]};
          }
          thead {
            display: none;
          }
          td {
            display: block;
            border: 0;
            padding: 0;
          }
          tr + tr td:first-child {
            border-top: 1px solid ${theme.palette.background.butterDark};
          }
          tr + tr:last-child td:last-child {
            padding-bottom: 0;
          }
          tr td:first-child {
            width: inherit;
            font-weight: ${theme.fontWeight.semibold};
            padding: ${theme.spacing[3]} 0 ${theme.spacing[2]};
          }
          tr td:last-child {
            padding-top: 0;
            padding-bottom: ${theme.spacing[3]};
          }
        }
      `}</style>
    </>
  )
}
